import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ThemeTokens } from '@/domain/types';
import { DEFAULT_THEME } from '@/domain/settings';
import { getDeviceSetting, setDeviceSetting } from '@/services/settings-service';
import {
  ensureFontLoaded,
  sanitizeThemeTokens,
  buildThemePalette,
  mixHex,
  rgba,
  type ThemePalette,
} from './theme-presets';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';
export type ThemeScope = 'company' | 'platform';

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  scope: ThemeScope;
  setPreference: (preference: ThemePreference, scopeOverride?: ThemeScope) => void;
  resetPreference: (scopeOverride?: ThemeScope) => void;
  applyBranding: (
    tokens: ThemeTokens,
    modeOverride?: ResolvedTheme,
    scopeOverride?: ThemeScope,
  ) => void;
}

function readPreference(key: string): ThemePreference | null {
  const stored = getDeviceSetting<ThemePreference | null>(key, null);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : null;
}

function preferenceKey(scope: ThemeScope): string {
  return scope === 'platform' ? 'platformThemeMode' : 'themeMode';
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_SNAPSHOT_KEY = 'taskflow.device.themeSnapshot';
const THEME_SNAPSHOT_VARS = [
  '--font', '--radius-sm', '--radius-md', '--radius-lg',
  '--primary', '--primary-strong', '--primary-soft', '--primary-contrast',
  '--accent', '--accent-soft', '--bg', '--surface', '--surface-2', '--surface-3',
  '--border', '--border-strong', '--text', '--text-2', '--muted', '--muted-2',
  '--sidebar-bg', '--sidebar-image', '--sidebar-text', '--sidebar-link', '--sidebar-muted',
  '--sidebar-border', '--sidebar-hover', '--sidebar-active-bg', '--sidebar-active-text',
  '--topbar-bg', '--bg-image', '--shadow-sm', '--shadow-md', '--shadow-lg',
  '--brand-icon-bg-1', '--brand-icon-bg-2', '--brand-icon-check', '--brand-icon-check-2',
  '--brand-icon-arrow', '--brand-icon-arrow-2', '--brand-icon-glow',
  '--auth-glow-primary', '--auth-glow-accent',
] as const;

function persistThemeSnapshot(root: HTMLElement, mode: ResolvedTheme): void {
  try {
    const vars = Object.fromEntries(
      THEME_SNAPSHOT_VARS.map((name) => [name, root.style.getPropertyValue(name)]),
    );
    localStorage.setItem(THEME_SNAPSHOT_KEY, JSON.stringify({ mode, vars }));
  } catch {
    // لا نُفشل الواجهة إذا كان التخزين المحلي غير متاح.
  }
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** ألوان أيقونة المنصة مشتقّة من الثيم حتى تتغيّر مع كل هوية بصرية. */
function brandIconColors(palette: ThemePalette, mode: ResolvedTheme) {
  const dark = mode === 'dark';
  return {
    bg1: mixHex('#061226', palette.primary, dark ? 0.3 : 0.24),
    bg2: mixHex('#020a18', palette.primary, dark ? 0.16 : 0.1),
    check: palette.primary,
    checkLight: mixHex(palette.primary, '#ffffff', 0.38),
    arrow: palette.accent,
    arrowLight: mixHex(palette.accent, '#ffffff', 0.32),
    glow: rgba(palette.primary, dark ? 0.34 : 0.26),
  };
}

/** يحدّث favicon وشريط المتصفح مع الثيم، لا الصورة المثبّتة على الشاشة الرئيسية. */
function applyBrowserBranding(palette: ThemePalette): void {
  if (typeof document === 'undefined') return;
  let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement('link');
    icon.rel = 'icon';
    document.head.appendChild(icon);
  }
  icon.type = 'image/png';
  icon.href = '/favicon.png?v=bed0cd2';

  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColor) themeColor.content = palette.bg;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [scope, setScope] = useState<ThemeScope>('company');
  const [devicePreferences, setDevicePreferences] = useState<
    Record<ThemeScope, ThemePreference | null>
  >(() => ({
    company: readPreference('themeMode'),
    platform: readPreference('platformThemeMode'),
  }));
  const [brandPreference, setBrandPreference] = useState<ThemePreference>('system');
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);
  const brandingRef = useRef<ThemeTokens>(DEFAULT_THEME);
  const hasAppliedBrandingRef = useRef(false);
  const devicePreferencesRef = useRef(devicePreferences);
  devicePreferencesRef.current = devicePreferences;

  const preference = devicePreferences[scope] ?? brandPreference;
  const resolved: ResolvedTheme = preference === 'system' ? system : preference;

  useEffect(() => {
    if (!window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystem(media.matches ? 'dark' : 'light');
    media.addEventListener?.('change', handler);
    return () => media.removeEventListener?.('change', handler);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  const setPreference = useCallback(
    (next: ThemePreference, scopeOverride?: ThemeScope) => {
      const target = scopeOverride ?? scope;
      setDevicePreferences((current) => ({ ...current, [target]: next }));
      setDeviceSetting(preferenceKey(target), next);
    },
    [scope],
  );

  const resetPreference = useCallback(
    (scopeOverride?: ThemeScope) => {
      const target = scopeOverride ?? scope;
      setDevicePreferences((current) => ({ ...current, [target]: null }));
      try {
        localStorage.removeItem(`taskflow.device.${preferenceKey(target)}`);
      } catch {
        // لا نُفشل الواجهة إذا كان التخزين المحلي غير متاح.
      }
    },
    [scope],
  );

  // كل ألوان الواجهة مشتقّة من ألوان الثيم: خلفية، أسطح، حدود، نصوص،
  // شريط جانبي، شريط علوي، ظلال، وخلفية زخرفية. تُطبَّق فورًا وعند تبديل الوضع.
  const applyModeVars = useCallback((tokens: ThemeTokens, mode: ResolvedTheme) => {
    const palette = buildThemePalette(tokens, mode);
    const root = document.documentElement;
    const vars: Record<string, string> = {
      '--primary': palette.primary,
      '--primary-strong': palette.primaryStrong,
      '--primary-soft': palette.primarySoft,
      '--primary-contrast': palette.primaryContrast,
      '--accent': palette.accent,
      '--accent-soft': palette.accentSoft,
      '--bg': palette.bg,
      '--surface': palette.surface,
      '--surface-2': palette.surface2,
      '--surface-3': palette.surface3,
      '--border': palette.border,
      '--border-strong': palette.borderStrong,
      '--text': palette.text,
      '--text-2': palette.text2,
      '--muted': palette.muted,
      '--muted-2': palette.muted2,
      '--sidebar-bg': palette.sidebarBg,
      '--sidebar-image': palette.sidebarImage,
      '--sidebar-text': palette.sidebarText,
      '--sidebar-link': palette.sidebarLink,
      '--sidebar-muted': palette.sidebarMuted,
      '--sidebar-border': palette.sidebarBorder,
      '--sidebar-hover': palette.sidebarHover,
      '--sidebar-active-bg': palette.sidebarActiveBg,
      '--sidebar-active-text': palette.sidebarActiveText,
      '--topbar-bg': palette.topbarBg,
      '--bg-image': palette.bgImage,
      '--shadow-sm': palette.shadowSm,
      '--shadow-md': palette.shadowMd,
      '--shadow-lg': palette.shadowLg,
    };
    const icon = brandIconColors(palette, mode);
    vars['--brand-icon-bg-1'] = icon.bg1;
    vars['--brand-icon-bg-2'] = icon.bg2;
    vars['--brand-icon-check'] = icon.check;
    vars['--brand-icon-check-2'] = icon.checkLight;
    vars['--brand-icon-arrow'] = icon.arrow;
    vars['--brand-icon-arrow-2'] = icon.arrowLight;
    vars['--brand-icon-glow'] = icon.glow;
    vars['--auth-glow-primary'] = rgba(palette.primary, mode === 'dark' ? 0.34 : 0.2);
    vars['--auth-glow-accent'] = rgba(palette.accent, mode === 'dark' ? 0.2 : 0.12);
    for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value);
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
    root.style.backgroundColor = palette.bg;
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.backgroundColor = palette.bg;
      document.body.style.color = palette.text;
    }
    persistThemeSnapshot(root, mode);
    applyBrowserBranding(palette);
  }, []);

  const applyBranding = useCallback(
    (tokens: ThemeTokens, modeOverride?: ResolvedTheme, scopeOverride: ThemeScope = 'company') => {
      const safe = sanitizeThemeTokens(tokens);
      brandingRef.current = safe;
      hasAppliedBrandingRef.current = true;
      const root = document.documentElement;
      root.style.setProperty('--font', safe.fontFamily || DEFAULT_THEME.fontFamily);
      ensureFontLoaded(safe.fontFamily);
      const radius = safe.radius === 'sm' ? '6px' : safe.radius === 'lg' ? '16px' : '10px';
      root.style.setProperty('--radius-sm', safe.radius === 'lg' ? '10px' : radius);
      root.style.setProperty('--radius-md', radius);
      root.style.setProperty('--radius-lg', safe.radius === 'sm' ? '10px' : '18px');
      // وضع كل مساحة مستقل: تفضيل المستخدم داخل المنصة لا يغيّر وضع الشركة،
      // والثيم المحفوظ هو المرجع ما لم يختر المستخدم فاتحًا أو داكنًا صراحةً.
      const preferred = modeOverride ?? devicePreferencesRef.current[scopeOverride] ?? safe.mode;
      const effective: ResolvedTheme = preferred === 'system' ? system : preferred;
      applyModeVars(safe, effective);
      setScope(scopeOverride);
      setBrandPreference(safe.mode);
    },
    [applyModeVars, system],
  );

  // إعادة تطبيق الألوان المرتبطة بالوضع عند تبديل فاتح/داكن.
  useEffect(() => {
    applyModeVars(brandingRef.current, resolved);
  }, [applyModeVars, resolved]);

  const value = useMemo(
    () => ({ preference, resolved, scope, setPreference, resetPreference, applyBranding }),
    [preference, resolved, scope, setPreference, resetPreference, applyBranding],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}

export function ThemeToggle() {
  const { resolved, setPreference } = useTheme();
  return (
    <button
      type="button"
      className="btn btn-icon"
      aria-label={resolved === 'dark' ? 'الوضع الفاتح' : 'الوضع الداكن'}
      title={resolved === 'dark' ? 'الوضع الفاتح' : 'الوضع الداكن'}
      onClick={() => setPreference(resolved === 'dark' ? 'light' : 'dark')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {resolved === 'dark' ? (
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path
            d="M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10M12 1v3M12 20v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M1 12h3M20 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"
            strokeLinecap="round"
          />
        )}
      </svg>
    </button>
  );
}
